import { caseStudies } from '@/content/caseStudies';
import { profile } from '@/content/profile';
import {
  buildBreadcrumbJsonLd,
  buildPersonJsonLd,
  buildWebSiteJsonLd,
  type JsonLdEntry,
} from '@/seo/jsonLd';
import { getRouteMeta } from '@/seo/routeMeta';
import { absoluteUrl, siteConfig } from '@/seo/siteConfig';

/**
 * Social cards need an absolute URL. The portrait is used as-is rather than
 * cropped to a landscape card: `summary` renders a square thumbnail, and
 * cropping this frame to 1.91:1 would cut the subject's head off.
 */
function socialImageTags(): MetaTag[] {
  const { photo } = profile;

  if (!photo) {
    return [];
  }

  return [
    { kind: 'property', key: 'og:image', content: absoluteUrl(photo.socialSrc) },
    { kind: 'property', key: 'og:image:width', content: String(photo.width) },
    { kind: 'property', key: 'og:image:height', content: String(photo.height) },
    { kind: 'property', key: 'og:image:alt', content: photo.alt },
    { kind: 'name', key: 'twitter:image', content: absoluteUrl(photo.socialSrc) },
    { kind: 'name', key: 'twitter:image:alt', content: photo.alt },
  ];
}

export interface MetaTag {
  /** `name` for standard meta tags, `property` for Open Graph. */
  kind: 'name' | 'property';
  key: string;
  content: string;
}

export interface PageMeta {
  title: string;
  description: string;
  canonical: string;
  noIndex: boolean;
  tags: readonly MetaTag[];
  jsonLd: readonly JsonLdEntry[];
}

export interface PageMetaOverrides {
  title?: string;
  description?: string;
  noIndex?: boolean;
}

function jsonLdForPath(pathname: string): JsonLdEntry[] {
  if (pathname === '/') {
    return [buildPersonJsonLd(), buildWebSiteJsonLd()];
  }

  const caseStudy = caseStudies.find((entry) => pathname === `/case-studies/${entry.slug}`);

  if (caseStudy) {
    return [
      buildBreadcrumbJsonLd([
        { name: 'Home', path: '/' },
        { name: 'Case studies', path: '/case-studies' },
        { name: caseStudy.title, path: `/case-studies/${caseStudy.slug}` },
      ]),
    ];
  }

  return [];
}

/**
 * The single source of page metadata.
 *
 * The `Seo` component renders this at runtime and the prerender step serialises
 * the same object into static HTML, so a crawler that never runs JavaScript and
 * a visitor navigating the SPA see identical metadata.
 */
export function buildPageMeta(pathname: string, overrides: PageMetaOverrides = {}): PageMeta {
  const routeMeta = getRouteMeta(pathname);

  const title = overrides.title ?? routeMeta.title;
  const description = overrides.description ?? routeMeta.description;
  const canonical = absoluteUrl(pathname);
  const noIndex = overrides.noIndex ?? routeMeta.noIndex ?? false;

  const tags: MetaTag[] = [
    { kind: 'name', key: 'description', content: description },
    { kind: 'property', key: 'og:type', content: 'website' },
    { kind: 'property', key: 'og:site_name', content: siteConfig.name },
    { kind: 'property', key: 'og:title', content: title },
    { kind: 'property', key: 'og:description', content: description },
    { kind: 'property', key: 'og:url', content: canonical },
    { kind: 'property', key: 'og:locale', content: 'en_GB' },
    { kind: 'name', key: 'twitter:card', content: 'summary' },
    { kind: 'name', key: 'twitter:title', content: title },
    { kind: 'name', key: 'twitter:description', content: description },
    ...socialImageTags(),
  ];

  if (noIndex) {
    tags.push({ kind: 'name', key: 'robots', content: 'noindex, follow' });
  }

  return { title, description, canonical, noIndex, tags, jsonLd: jsonLdForPath(pathname) };
}
